/**
 * magneticPipeline — iron filings around magnetic poles.
 *
 * Visualizes magnetic field lines as flowing curves. We place 2
 * magnetic poles (north and south) at fixed positions and compute
 * the field-line integral for many seed points. Each line is colored
 * by its z-component (going up = positive, down = negative).
 *
 * The lines emanate from one pole and arc toward the other, like iron
 * filings on a piece of paper above a bar magnet.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MagneticPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Trace a magnetic field line from a seed point. Returns the
// minimum distance from the current pixel to the line.
float traceFieldLine(vec2 pixel, vec2 start, int steps) {
  // Two magnetic poles.
  vec2 poleA = vec2(-0.5 + 0.1 * sin(uTime * 0.5), 0.0);
  vec2 poleB = vec2( 0.5 + 0.1 * cos(uTime * 0.5), 0.0);
  // Pole strengths.
  float strengthA = 1.0;
  float strengthB = -1.0;
  float minDist = 100.0;
  vec2 p = start;
  for (int i = 0; i < 100; i++) {
    if (i >= steps) break;
    // Compute field at p.
    vec2 toA = poleA - p;
    vec2 toB = poleB - p;
    float distA = max(length(toA), 0.1);
    float distB = max(length(toB), 0.1);
    vec2 field = toA * (strengthA / (distA * distA * distA)) +
                 toB * (strengthB / (distB * distB * distB));
    // Step along field.
    p += field * 0.05;
    float d = length(pixel - p);
    minDist = min(minDist, d);
  }
  return minDist;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Background: dark paper with subtle texture.
  float bgNoise = hash21(floor(gl_FragCoord.xy / 4.0));
  vec3 col = vec3(0.06, 0.05, 0.08) + vec3(0.02) * bgNoise;

  // Seed positions for field lines: ring around the north pole.
  vec2 poleA = vec2(-0.5 + 0.1 * sin(uTime * 0.5), 0.0);
  vec2 poleB = vec2( 0.5 + 0.1 * cos(uTime * 0.5), 0.0);
  const int N_LINES = 18;
  for (int i = 0; i < N_LINES; i++) {
    float fi = float(i);
    float angle = fi * 6.2831853 / float(N_LINES) + uTime * 0.1;
    float radius = 0.05 + 0.05 * hash21(vec2(fi, 1.7));
    vec2 start = poleA + vec2(cos(angle), sin(angle)) * radius;
    float d = traceFieldLine(p, start, 60);
    // Glow around the line.
    float glow = exp(-d * d * 8000.0);
    // Color: blue (going in) → red (going out) based on angle.
    float hue = fi / float(N_LINES);
    vec3 lineColor = vec3(
      0.5 + 0.5 * sin(hue * 6.2831853),
      0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
      0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
    );
    // Brighten with audio.
    col += lineColor * glow * (0.8 + bass * 0.5);
  }

  // Poles: bright dots at the magnetic poles.
  float dA = length(p - poleA);
  float dB = length(p - poleB);
  col += vec3(1.0, 0.3, 0.2) * exp(-dA * dA * 500.0) * (1.0 + high * 0.5);
  col += vec3(0.2, 0.4, 1.0) * exp(-dB * dB * 500.0) * (1.0 + high * 0.5);

  // Vignette.
  float r = length(p);
  float vig = 1.0 - r * r * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeMagneticBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeMagneticRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMagneticPipeline(gl: WebGL2RenderingContext): MagneticPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMagnetic(
  gl: WebGL2RenderingContext,
  pipe: MagneticPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
